/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axiosClient from '../../api/axiosClient';
import PortalModal from '../../components/shared/PortalModal';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, subtitle, icon }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
  </div>
);

const RescheduleModal = ({ open, onClose, onSubmit, booking, isDark, user }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [shopTimezone, setShopTimezone] = useState('');
  const [availabilityStatus, setAvailabilityStatus] = useState({ valid: null, message: '' });

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open && booking) {
      setSelectedDate('');
      setSelectedTime('');
      setError('');
      setAvailabilityStatus({ valid: null, message: '' });
      
      // Load shop timezone
      loadShopTimezone();
    } else {
      setSelectedDate('');
      setSelectedTime('');
      setError('');
      setAvailabilityStatus({ valid: null, message: '' });
    }
  }, [open, booking]);

  // Load shop timezone
  const loadShopTimezone = async () => {
    try {
      const response = await axiosClient.get(`/slots?barber_id=${booking.barber.id}&start_date=${new Date().toISOString().split('T')[0]}&days=1`);
      const timezone = response.data.timezone;
      setShopTimezone(timezone);
    } catch (error) {
      console.error('Error loading timezone:', error);
    }
  };

  // Simple validation for basic business rules
  const validateTimeSelection = (date, time) => {
    if (!date || !time) {
      return { valid: false, message: 'Please select both date and time' };
    }

    const selectedDateTime = new Date(`${date}T${time}`);
    const now = new Date();
    const minLeadTimeDays = booking?.service?.lead_time_days || 0;
    const minDate = new Date();
    minDate.setDate(minDate.getDate() + minLeadTimeDays);

    // Check if date is in the past (considering lead time)
    if (selectedDateTime < minDate) {
      return { 
        valid: false, 
        message: `Date must be at least ${minLeadTimeDays} days in advance` 
      };
    }

    // Check if it's the same time as current booking
    const currentBookingTime = new Date(booking.start_local);
    if (selectedDateTime.getTime() === currentBookingTime.getTime()) {
      return { 
        valid: false, 
        message: 'Please select a different time from the current booking' 
      };
    }

    return { valid: true, message: 'Time selection looks good' };
  };

  // Handle date change
  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedTime('');
    setError('');
    setAvailabilityStatus({ available: null, reason: '' });
  };

  // Handle time change
  const handleTimeChange = (time) => {
    setSelectedTime(time);
    
    // Simple validation
    const validation = validateTimeSelection(selectedDate, time);
    if (validation.valid) {
      setError('');
      setAvailabilityStatus({ valid: true, message: validation.message });
    } else {
      setError(validation.message);
      setAvailabilityStatus({ valid: false, message: validation.message });
    }
  };

  // Get minimum date (today + lead time)
  const getMinDate = () => {
    const today = new Date();
    const leadTimeDays = booking?.service?.lead_time_days || 0;
    today.setDate(today.getDate() + leadTimeDays);
    return today.toISOString().split('T')[0];
  };

  // Get maximum date (today + advance booking limit)
  const getMaxDate = () => {
    const today = new Date();
    const maxAdvanceDays = 60; // Default advance booking limit
    today.setDate(today.getDate() + maxAdvanceDays);
    return today.toISOString().split('T')[0];
  };

  // Generate time options based on business hours
  const generateTimeOptions = () => {
    const options = [];
    
    // Default business hours (9 AM to 6 PM)
    const startHour = 9;
    const endHour = 18;
    const slotDuration = 30; // minutes
    
    for (let hour = startHour; hour < endHour; hour++) {
      for (let minutes = 0; minutes < 60; minutes += slotDuration) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        options.push(timeString);
      }
    }
    
    return options;
  };

  // Format date for display
  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Format time for display
  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime) {
      setError('Please select both date and time');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Convert selected local time to UTC
      const localDateTime = new Date(`${selectedDate}T${selectedTime}`);
      const utcDateTime = localDateTime.toISOString();

      await onSubmit(utcDateTime);
      
      // Success - parent will close modal
    } catch (error) {
      console.error('Error rescheduling booking:', error);
      
      // Handle specific error cases
      if (error.response?.data?.error) {
        const errorType = error.response.data.error;
        const errorMessage = error.response.data.message;
        
        switch (errorType) {
          case 'BOOKING_CONFLICT':
            setError('This time slot conflicts with another booking');
            break;
          case 'PAST_TIME':
            setError('Cannot reschedule to a past time');
            break;
          case 'LEAD_TIME_NOT_MET':
            setError(`This time does not meet the minimum ${booking?.service?.lead_time_days || 0} days lead time requirement`);
            break;
          case 'MAX_ADVANCE_EXCEEDED':
            setError('Cannot reschedule this far in advance');
            break;
          case 'INVALID_SLOT_ALIGNMENT':
            setError('Selected time does not align with available slots');
            break;
          default:
            setError(errorMessage || 'Failed to reschedule booking');
        }
      } else {
        setError('Failed to reschedule booking');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <PortalModal open={open}>
      <div className="hc-modal-overlay bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center">
        <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="hc-modal-panel p-4 md:p-6 rounded-2xl max-w-2xl w-full my-8 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10 shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">Reschedule Booking</h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                Choose a new date and time for this appointment
              </p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleClose}
            disabled={submitting}
            className="p-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ✕
          </motion.button>
        </div>

        {/* Current Booking Summary */}
        <Card className="mb-6">
          <CardHeader
            title="Current Booking Details"
            subtitle="Original appointment information"
            icon={
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            }
          />
          <div className="p-4 md:p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                  Client
                </label>
                <div className="text-zinc-900 dark:text-zinc-100">{booking?.client?.name || 'Unknown'}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                  Barber
                </label>
                <div className="text-zinc-900 dark:text-zinc-100">{booking?.barber?.name || 'Unknown'}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                  Service
                </label>
                <div className="text-zinc-900 dark:text-zinc-100">
                  {booking?.service?.name || 'Unknown'} ({booking?.service?.duration_min || 60} min)
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                  Current Time
                </label>
                <div className="text-zinc-900 dark:text-zinc-100">
                  {booking?.start_local ? formatTime(booking.start_local) : 'N/A'}
                </div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                  {booking?.start_local ? formatDate(booking.start_local) : ''}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Date and Time Selection */}
        <Card className="mb-6">
          <CardHeader
            title="Select New Date & Time"
            subtitle="Choose a new appointment time"
            icon={
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <div className="p-4 md:p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Date Selection */}
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                  Select Date *
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                  min={getMinDate()}
                  max={getMaxDate()}
                  className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                  style={{ '--accent': ACCENT }}
                />
              </div>

              {/* Time Selection */}
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                  Select Time *
                </label>
                <select
                  value={selectedTime}
                  onChange={(e) => handleTimeChange(e.target.value)}
                  disabled={!selectedDate}
                  className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ '--accent': ACCENT }}
                >
                  <option value="">
                    {selectedDate ? 'Choose a time...' : 'Select date first'}
                  </option>
                  {selectedDate && generateTimeOptions().map((time, index) => (
                    <option key={index} value={time}>
                      {new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit',
                        hour12: true 
                      })}
                    </option>
                  ))}
                </select>
              </div>
            </div>


            {/* Validation Status */}
            {availabilityStatus.valid === true && (
              <div className="mt-4 flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {availabilityStatus.message}
              </div>
            )}

            {/* Timezone Display */}
            <div className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
              All times shown in {shopTimezone || 'local time'}
            </div>
          </div>
        </Card>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-400 rounded-xl">
            {error}
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex justify-end gap-3">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleSubmit}
            disabled={!selectedDate || !selectedTime || submitting}
            className="px-4 py-2 rounded-xl text-white transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ 
              background: (!selectedDate || !selectedTime || submitting) ? '#9CA3AF' : ACCENT,
              ':hover': { background: (!selectedDate || !selectedTime || submitting) ? '#9CA3AF' : '#6d5a2a' }
            }}
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Rescheduling...
              </span>
            ) : (
              'Confirm Reschedule'
            )}
          </motion.button>
        </div>
        </motion.div>
      </div>
    </PortalModal>
  );
};

export default RescheduleModal;